// packages/console/src/components/LiveHlsPlayer.tsx
//
// A minimal, self-contained HLS playback surface for a live ABR MASTER manifest
// reached by a SELF-AUTHENTICATING url (a minted playback URL that carries its
// own token in the query string). This is the same hls.js playback core the file
// PlayerOverlay uses in master mode, distilled to the one thing the live "Play"
// affordance needs: load ONE master playlist through ONE hls.js instance and let
// hls.js do ABR across the ladder (no per-rung reload, no quality selector).
//
// It attaches NO Authorization header: unlike the in-console LiveProgramMonitor
// (which proxies through the authenticated live proxy with the in-memory bearer),
// this player runs in a context that does not hold the session token (a new tab
// opened by window.open, where the in-memory access token does not exist). The
// minted url authenticates itself, so hls.js resolves the master AND its relative
// variant/segment requests without a header.
//
// Honest failure (R10): a load error surfaces a plain message rather than a
// black rectangle, and Safari's native HLS is used when hls.js is unsupported.

import { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { rollingKbps, fmtKbps, type BitrateSample } from "../lib/bitrate";

export function LiveHlsPlayer({ url }: { url: string }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Live delivered bitrate (rolling avg of buffered fragments) + current
  // rendition height, painted as a corner overlay. null until the first
  // fragment buffers (or on Safari's native path, which exposes no frag stats).
  const [kbps, setKbps] = useState<number | null>(null);
  const [heightPx, setHeightPx] = useState<number | null>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    setErr(null);
    setKbps(null);
    setHeightPx(null);
    const samples: BitrateSample[] = [];

    // hls.js path (Chrome/Firefox/Edge): one instance for the whole session,
    // native ABR left on so the player switches renditions off the master.
    if (Hls.isSupported()) {
      // Start as close to the LIVE EDGE as HLS allows and stay there. With a plain
      // (non-LL) HLS playlist the floor is one segment, so pin the sync target one
      // segment back (liveSyncDurationCount: 1), keep no back-buffer, and let the
      // player nudge its rate up to catch the edge if it drifts. On the first level
      // load we seek to the newest segment (the end of the level's fragment list,
      // falling back to liveSyncPosition) rather than mid-window.
      const hls = new Hls({
        lowLatencyMode: true,
        liveSyncDurationCount: 1,
        maxLiveSyncPlaybackRate: 1.5,
        backBufferLength: 0,
      });
      let jumpedToEdge = false;
      hls.on(Hls.Events.LEVEL_LOADED, (_evt, data) => {
        // Capture the active rendition height for the overlay label.
        const h = hls.levels[data.level]?.height;
        if (h) setHeightPx(h);
        if (jumpedToEdge || !data.details.live) return;
        // Seek to the newest fragment's start: the tightest a plain live playlist
        // gets. Fall back to hls.liveSyncPosition if the fragment list is absent.
        const frags = data.details.fragments;
        const newest = frags && frags.length ? frags[frags.length - 1].start : hls.liveSyncPosition;
        if (newest != null && Number.isFinite(newest)) {
          jumpedToEdge = true;
          video.currentTime = newest;
          // Autostart. Browsers only allow autoplay when muted, so the player is
          // muted (the live source carries no audio anyway); a Mute control on the
          // native <video> lets the operator unmute if a future source has sound.
          void video.play().catch(() => {});
        }
      });
      // Measure the ACTUAL delivered bitrate from each buffered fragment's size
      // and duration (the real VBR figure, not the declared cap), averaged over a
      // short rolling window so it tracks scene changes but stays readable.
      hls.on(Hls.Events.FRAG_BUFFERED, (_evt, data) => {
        const bytes = data.frag?.stats?.total ?? data.stats?.total;
        const seconds = data.frag?.duration;
        if (bytes && seconds && seconds > 0) {
          samples.push({ bytes, seconds });
          if (samples.length > 16) samples.shift();
          setKbps(rollingKbps(samples));
        }
      });
      hls.on(Hls.Events.ERROR, (_evt, data) => {
        if (data.fatal) setErr("Could not play this live stream. The link may have expired.");
      });
      hls.loadSource(url);
      hls.attachMedia(video);
      return () => {
        hls.destroy();
      };
    }

    // Native HLS (Safari): the self-authenticating url is set directly; Safari's
    // media stack cannot inject a header, which is exactly why the minted url
    // carries its own token. Seek to the live edge once metadata is in.
    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = url;
      const toEdge = () => {
        const s = video.seekable;
        if (s.length) video.currentTime = s.end(s.length - 1);
        video.removeEventListener("loadedmetadata", toEdge);
      };
      video.addEventListener("loadedmetadata", toEdge);
      return () => video.removeEventListener("loadedmetadata", toEdge);
    }

    setErr("This browser cannot play HLS. Copy the URL and open it in VLC.");
  }, [url]);

  return (
    <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "#000" }}>
      {err ? (
        <div style={{ padding: 40, color: "var(--err)", font: "var(--t-body-sm)", textAlign: "center" }}>{err}</div>
      ) : (
        <>
          <video
            ref={videoRef}
            controls
            autoPlay
            muted
            playsInline
            style={{ width: "100%", height: "100%", objectFit: "contain", background: "#000" }}
          />
          {/* Live delivered-bitrate overlay: the real VBR figure measured off the
              buffered fragments, updating each segment. Shown once the first
              fragment buffers. */}
          {kbps != null && (
            <div
              style={{
                position: "absolute",
                top: 12,
                right: "10%",
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "5px 10px",
                borderRadius: 6,
                background: "rgba(0,0,0,0.8)",
                color: "#fff",
                font: "600 12px ui-monospace, monospace",
                fontVariantNumeric: "tabular-nums",
                letterSpacing: ".02em",
                pointerEvents: "none",
                userSelect: "none",
              }}
            >
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#f43f5e" }} />
              {fmtKbps(kbps)}
              {heightPx != null && (
                <span style={{ color: "#fff" }}>
                  <span style={{ opacity: 0.5 }}>· </span>
                  {heightPx}p
                </span>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
